/**
 * Event Detail Modal
 * Rich event inspection with all Superpowers visible
 */
import React from 'react';
import { 
  X, 
  Shield, 
  User, 
  Clock, 
  FileText, 
  Link as LinkIcon, 
  CheckCircle2, 
  Hash,
  ExternalLink,
  Code
} from 'lucide-react';

export function EventDetailModal({ event, isOpen, onClose }) {
  if (!isOpen || !event) return null;

  const eventType = event.event_type || event.eventType || '';
  const actor = event.actor || {};
  const actorName = actor.user_name || event.actorUserName || 'System';
  const actorRole = actor.user_role || event.actorUserRole || 'N/A';
  const actorEmail = actor.user_email || event.actorUserEmail || 'N/A';
  const timestamp = event.event_timestamp || event.eventTimestamp;
  const chainIndex = event.chain_index || event.chainIndex || 0;
  const eventHash = event.event_hash || event.eventHash || '';
  const prevHash = event.previous_event_hash || event.previousEventHash || '';
  const aggregateType = event.aggregate_type || event.aggregateType || 'Unknown';
  const aggregateId = event.aggregate_id || event.aggregateId || '';
  const aggregateName = event.aggregate_name || event.aggregateName || '';
  const eventData = event.event_data || event.eventData || {};
  const crossFrameworkImpact = event.cross_framework_impact || event.crossFrameworkImpact || [];

  return (
    <div 
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="event-detail-modal"
    >
      <div 
        className="bg-[#0A0A0A] rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-[#1F1F1F]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white p-6 shrink-0">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 rounded bg-white/20 text-white text-xs">
                  {eventType}
                </span>
                <span className="px-2 py-1 rounded bg-white/20 text-white text-xs">
                  {aggregateType}
                </span>
                <span className="px-2 py-1 rounded bg-white/20 text-white text-xs font-mono">
                  Chain #{chainIndex}
                </span>
              </div>
              <h2 className="text-xl font-bold">Audit Event Details</h2>
              <p className="text-sm text-white/80 mt-1">
                {new Date(timestamp).toLocaleString()}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded transition-colors"
              data-testid="close-modal-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Actor Section */}
          <div className="section">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <User className="h-4 w-4 text-emerald-400" />
              Actor Information
            </h3>
            <div className="grid grid-cols-2 gap-4 p-4 bg-[#111111] rounded-lg border border-[#1F1F1F]">
              <div>
                <p className="text-xs text-[#6B6B6B]">Name</p>
                <p className="text-sm font-medium text-white">{actorName}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B]">Role</p>
                <p className="text-sm font-medium text-white">{actorRole}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B]">Email</p>
                <p className="text-sm font-medium text-white">{actorEmail}</p>
              </div>
              <div>
                <p className="text-xs text-[#6B6B6B]">IP Address</p>
                <p className="text-sm font-mono text-[#A0A0A0]">
                  {actor.ip_address || event.actorIpAddress || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Entity Section */}
          <div className="section">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-400" />
              Affected Entity
            </h3>
            <div className="p-4 bg-[#111111] rounded-lg border border-[#1F1F1F]">
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <p className="text-xs text-[#6B6B6B]">Type</p>
                  <p className="text-sm font-medium text-white">{aggregateType}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6B6B6B]">ID</p>
                  <p className="text-sm font-mono text-[#A0A0A0]">
                    {aggregateId?.substring(0, 16)}...
                  </p>
                </div>
              </div>
              {aggregateName && (
                <div>
                  <p className="text-xs text-[#6B6B6B] mb-1">Name</p>
                  <p className="text-sm font-medium text-white">{aggregateName}</p>
                </div>
              )}
            </div>
          </div>

          {/* Cross-Framework Impact (Superpower #1) */}
          {crossFrameworkImpact.length > 0 && (
            <div className="section">
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <LinkIcon className="h-4 w-4 text-teal-400" />
                Cross-Framework Impact
              </h3>
              <div className="space-y-2 p-4 bg-teal-500/10 rounded-lg border border-teal-500/20">
                {crossFrameworkImpact.map((impact, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-[#111111] border border-[#1F1F1F]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-white">
                          {impact.framework_code || impact.frameworkCode} {impact.requirement_code || impact.requirementCode}
                        </p>
                        <p className="text-xs text-[#6B6B6B]">
                          {(impact.impact_type || impact.impactType || '').replace(/_/g, ' ')}
                        </p>
                      </div>
                      <ExternalLink className="h-4 w-4 text-[#6B6B6B]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cryptographic Proof (Superpower #3) */}
          <div className="section">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Shield className="h-4 w-4 text-emerald-400" />
              Cryptographic Verification
            </h3>
            <div className="space-y-3 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
              <div>
                <p className="text-xs font-semibold text-[#A0A0A0] mb-1">
                  Event Hash (SHA-256)
                </p>
                <div className="p-2 bg-[#111111] rounded border border-[#1F1F1F] break-all">
                  <p className="text-xs font-mono text-white">{eventHash || 'N/A'}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-[#A0A0A0] mb-1">
                  Previous Event Hash
                </p>
                <div className="p-2 bg-[#111111] rounded border border-[#1F1F1F] break-all">
                  <p className="text-xs font-mono text-white">{prevHash || 'Genesis Block'}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/20 border border-emerald-500/30">
                <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-emerald-400">
                    ✓ Integrity Verified
                  </p>
                  <p className="text-xs text-emerald-300">
                    Hash matches recorded hash. No tampering detected.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Event Data */}
          <div className="section">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Code className="h-4 w-4 text-purple-400" />
              Event Data Payload
            </h3>
            <pre className="text-xs bg-[#111111] text-emerald-400 p-4 rounded-lg overflow-x-auto border border-[#1F1F1F] font-mono">
              {JSON.stringify(eventData, null, 2)}
            </pre>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#1F1F1F] p-4 shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default EventDetailModal;
